import React from "react";
import PropTypes from "prop-types";
import { ReactSelect } from "@/components/ui/react-select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Modal from "@/components/ui/modal";
import { Filter, RotateCcw, Check, Tag, Info } from "lucide-react";

const FilterModal = ({
  show,
  onHide,
  brands,
  selectedBrand,
  handleBrandChange,
  currencySymbol,
  priceFilter,
  setPriceFilter,
  clearFilters,
  applyFilters,
}) => {
  return (
    <Modal
      open={show}
      onOpenChange={(open) => !open && onHide()}
      title="Filter Products"
      icon={Filter}
      maxWidth="max-w-md"
      footer={
        <div className="flex gap-3 justify-end w-full">
          <Button variant="outline" onClick={clearFilters} className="flex-1">
            <RotateCcw className="mr-2 h-4 w-4" />
            Reset
          </Button>
          <Button onClick={applyFilters} className="flex-1">
            <Check className="mr-2 h-4 w-4" />
            Apply
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        <div className="space-y-2">
          <Label className="font-bold flex items-center gap-1.5">
            <Tag className="h-4 w-4 text-primary" />
            Brand
          </Label>
          <ReactSelect
            options={[
              { value: null, label: "All Brands" },
              ...brands.map((brand) => ({
                value: brand.id,
                label: brand.name,
              })),
            ]}
            placeholder="Select Brand"
            value={
              selectedBrand
                ? {
                  value: selectedBrand,
                  label:
                    brands.find((b) => b.id === selectedBrand)?.name ||
                    "Selected Brand",
                }
                : null
            }
            onChange={handleBrandChange}
            isClearable
          />
        </div>

        <div className="space-y-2">
          <Label className="font-bold">Price Range</Label>
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                {currencySymbol}
              </span>
              <Input
                type="number"
                placeholder="Min"
                className="pl-7"
                min="0"
                step="0.01"
                value={priceFilter.min}
                onChange={(e) =>
                  setPriceFilter({
                    ...priceFilter,
                    min: e.target.value,
                  })
                }
              />
            </div>
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                {currencySymbol}
              </span>
              <Input
                type="number"
                placeholder="Max"
                className="pl-7"
                min="0"
                step="0.01"
                value={priceFilter.max}
                onChange={(e) =>
                  setPriceFilter({
                    ...priceFilter,
                    max: e.target.value,
                  })
                }
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Enter minimum and/or maximum price to filter products
          </p>
        </div>

        {(selectedBrand || priceFilter.min || priceFilter.max) && (
          <div className="rounded-lg bg-blue-50 p-3 flex items-center justify-between text-blue-700 text-sm">
            <div className="flex items-center gap-2">
              <Info className="h-4 w-4" />
              <span>
                Active Filters:{" "}
                {[
                  selectedBrand ? "Brand" : "",
                  priceFilter.min || priceFilter.max ? "Price" : "",
                ]
                  .filter(Boolean)
                  .join(", ")}
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-blue-700 hover:text-blue-800 hover:bg-blue-100"
              onClick={clearFilters}
            >
              Clear All
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
};

FilterModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  brands: PropTypes.array,
  selectedBrand: PropTypes.any,
  handleBrandChange: PropTypes.func.isRequired,
  currencySymbol: PropTypes.string,
  priceFilter: PropTypes.object.isRequired,
  setPriceFilter: PropTypes.func.isRequired,
  clearFilters: PropTypes.func.isRequired,
  applyFilters: PropTypes.func.isRequired,
};

export default FilterModal;
